import { useState, useCallback, useEffect } from 'react';
import { startMfaEnrollment, confirmMfaEnrollment } from '../services/api';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'scan' | 'verify' | 'success';

interface EnrollData {
  qrCode: string;
  secret: string;
}

// MFA enrolment flow — TOTP (RFC 6238), vérifié par le serveur Express.
// Steps:
//   1. POST /api/auth/mfa/enroll → QR code (data URL) + secret en clair
//   2. User scans the QR code with Google Authenticator / Authy / 1Password / etc.
//   3. User enters the 6-digit TOTP code
//   4. POST /api/auth/mfa/enroll/confirm { code } → la 2FA est active, et le
//      jeton courant est élevé (pas de déconnexion à l'activation).
//
// Fermer la modale avant la confirmation n'active rien : le secret provisoire
// est simplement remplacé au prochain démarrage d'enrôlement.

export default function MfaEnrollModal({ visible, onClose, onSuccess }: Props) {
  const [step, setStep] = useState<Step>('scan');
  const [enrollData, setEnrollData] = useState<EnrollData | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start enrolment when the modal opens
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setCode('');
      setStep('scan');
      setEnrollData(null);
      try {
        const data = await startMfaEnrollment();
        if (cancelled) return;
        setEnrollData({ qrCode: data.qrCode, secret: data.secret });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Erreur lors du démarrage de l'activation");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [visible]);

  const handleCancel = useCallback(() => {
    setEnrollData(null);
    setCode('');
    setStep('scan');
    setError(null);
    onClose();
  }, [onClose]);

  const handleVerify = useCallback(async () => {
    if (!enrollData) return;
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError('Le code doit contenir exactement 6 chiffres.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await confirmMfaEnrollment(code);
      setStep('success');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Code invalide');
      setCode('');
    } finally {
      setLoading(false);
    }
  }, [enrollData, code]);

  const handleFinish = useCallback(() => {
    setEnrollData(null);
    setCode('');
    setStep('scan');
    setError(null);
    onSuccess();
  }, [onSuccess]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
              {step === 'success' ? 'Double authentification activée' : 'Activer la double authentification'}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {step === 'success'
                ? 'Votre compte est désormais protégé par un second facteur.'
                : 'Renforcez la sécurité de votre compte avec une application d\'authentification.'}
            </p>
          </div>
          <button
            onClick={handleCancel}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xl leading-none shrink-0"
          >
            &times;
          </button>
        </div>

        {/* ── Step: scan QR code ── */}
        {step === 'scan' && (
          <div className="space-y-4">
            <ol className="text-xs text-slate-600 dark:text-slate-400 space-y-1 list-decimal pl-4">
              <li>Ouvrez votre application d'authentification (Google Authenticator, Authy, 1Password, Bitwarden, etc.)</li>
              <li>Scannez le QR code ci-dessous ou saisissez la clé manuellement</li>
              <li>Entrez le code à 6 chiffres généré par votre application</li>
            </ol>

            {loading && !enrollData && (
              <div className="flex items-center justify-center py-8">
                <svg className="w-8 h-8 text-violet-500 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}

            {!enrollData && !loading && error && <p className="text-sm text-red-500">{error}</p>}

            {enrollData && (
              <>
                <div className="flex justify-center bg-white p-4 rounded-lg border border-slate-200">
                  {/* Le serveur renvoie le QR en PNG (data URL) */}
                  <img src={enrollData.qrCode} alt="QR code 2FA" className="w-48 h-48" />
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Ou saisissez cette clé manuellement&nbsp;:</p>
                  <code className="text-xs font-mono text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded select-all break-all inline-block">
                    {enrollData.secret}
                  </code>
                </div>

                {error && <p className="text-sm text-red-500">{error}</p>}

                <button
                  onClick={() => setStep('verify')}
                  className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 transition-all"
                >
                  J'ai scanné le QR code
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Step: verify code ── */}
        {step === 'verify' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Entrez le code à 6 chiffres affiché par votre application d'authentification.
            </p>

            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => {
                if (e.key === 'Enter' && code.length === 6) handleVerify();
              }}
              autoFocus
              placeholder="000000"
              disabled={loading}
              className="w-full px-4 py-3 text-center text-2xl tracking-[0.4em] font-mono rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
            />

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setStep('scan'); setError(null); setCode(''); }}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                Retour
              </button>
              <button
                onClick={handleVerify}
                disabled={loading || code.length !== 6}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {loading ? 'Vérification...' : 'Valider'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step: success ── */}
        {step === 'success' && (
          <div className="space-y-4">
            <div className="flex items-center justify-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
              Lors de votre prochaine connexion, un code à 6 chiffres vous sera demandé en plus de votre mot de passe. Conservez précieusement l'accès à votre application d'authentification.
            </p>
            <button
              onClick={handleFinish}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 transition-all"
            >
              Terminé
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
